import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";
import { hasWritePermission } from '../../utils/menuFilter';

const PaymentMode = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [paymentModeData, setPaymentModeData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingMode, setEditingMode] = useState(null);
  const [newMode, setNewMode] = useState({
    modeName: "",
    status: "Active",
  });

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedModeForDelete, setSelectedModeForDelete] = useState(null);

  const fetchPaymentModes = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=paymentmode`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();

      const paymentModes = Array.isArray(responseData.data)
        ? responseData.data.map((item) => ({
          id: item._id,
          modeName: item.details?.paymentMode || item.details?.modeName || "Unnamed Payment Mode",
          status: item.status === true ? "Active" : "Inactive",
          rawStatus: item.status,
        }))
        : [];
      setPaymentModeData(paymentModes);
    } catch (error) {
      setApiError("Failed to fetch payment modes. Please try again later.");
      console.error("Error fetching payment modes:", error);
      setPaymentModeData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentModes();
  }, []);

  const getFilteredModes = () => {
    if (!Array.isArray(paymentModeData)) {
      return [];
    }

    return paymentModeData.filter((mode) => {
      const matchesSearch = mode.modeName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || mode.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newMode.modeName.trim()) newErrors.modeName = "Payment mode name is required";
    return newErrors;
  };

  const handleEdit = (mode) => {
    setEditingMode(mode);
    setNewMode({
      modeName: mode.modeName || "",
      status: mode.status || "Active",
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (mode) => {
    setSelectedModeForDelete(mode);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedModeForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedModeForDelete.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error("Delete failed");

      setAlertConfig({ title: 'Success', message: 'Payment mode deleted successfully!' });
      setAlertOpen(true);
      fetchPaymentModes(true);
    } catch (error) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete payment mode. Please try again.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedModeForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPage(0);
    setErrors({});
    fetchPaymentModes(true);
  };

  const handleOpenModal = () => {
    setEditingMode(null);
    setNewMode({
      modeName: "",
      status: "Active",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMode(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMode({ ...newMode, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddMode = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        type: "paymentmode",
        details: {
          paymentMode: newMode.modeName.trim(),
        },
        status: newMode.status === "Active",
      };

      const url = editingMode
        ? `${baseURL}/api/globalMasterDetails/${editingMode.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingMode ? "update" : "add"} payment mode: ${response.statusText}`
        );
      }

      setAlertConfig({
        title: 'Success',
        message: editingMode ? "Payment mode updated successfully!" : "Payment mode added successfully!"
      });
      setAlertOpen(true);
      await fetchPaymentModes(true);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: error.message || "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredModes = getFilteredModes();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PaymentIcon sx={{ mr: 1, color: "primary.main", fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Payment Mode Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchPaymentModes()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600, flex: { xs: 1, sm: 'none' }, textTransform: 'none' }}
            >
              Add Payment Mode
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal Alerts & Confirms */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the payment mode <strong>{selectedModeForDelete?.modeName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {/* API Error Message */}
      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchPaymentModes()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Payment Modes..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Filter by Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  const count = getFilteredModes().length;
                  setAlertConfig({
                    title: 'Search Results',
                    message: count > 0 ? `Found ${count} payment modes matching your criteria.` : 'No payment modes found matching your criteria.'
                  });
                  setAlertOpen(true);
                }}
                startIcon={<SearchIcon />}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, height: '56px' }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                fullWidth={false}
                onClick={handleReset}
                sx={{ flex: 1, textTransform: "none", height: "56px", borderRadius: 2, fontWeight: 600 }}
              >
                Reset
              </Button>
            </Box>
        </Grid>
      </Paper>

      {/* Table Section */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Payment Mode</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredModes.length > 0 ? (
                  filteredModes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((mode, index) => (
                      <TableRow key={mode.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{mode.modeName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={mode.status}
                            size="small"
                            color={mode.status === "Active" ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canWrite && (
                              <Tooltip title="Edit Payment Mode">
                                <IconButton color="primary" size="small" onClick={() => handleEdit(mode)} disabled={isLoading}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWrite && (
                              <Tooltip title="Delete Payment Mode">
                                <IconButton color="error" size="small" onClick={() => handleDeleteClick(mode)} disabled={isLoading}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No payment modes found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredModes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Add/Edit Payment Mode Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editingMode ? "Edit Payment Mode" : "Add New Payment Mode"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Mode Name"
                name="modeName"
                value={newMode.modeName}
                onChange={handleInputChange}
                error={!!errors.modeName}
                helperText={errors.modeName || "e.g., Cash, Cheque, UPI, Credit Card, etc."}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newMode.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddMode}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "Saving..." : editingMode ? "Update Mode" : "Add Mode"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMode;